// src/components/common/CustomerInfoForm/CustomerInfoForm.jsx

import React, { useState, useEffect } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import { useUser } from "../../../context/UserContext";
import { fetchStoreCustomerProfile } from "../../../firebase/customer-profile-functions";
import AuthPage from "../../../pages/customer/auth/AuthPage";
import "./CustomerInfoForm.css";

const CustomerInfoForm = ({
  formData,
  setFormData,
  storeSettings,
  processing = false,
  showCreateAccount = false,
  showUseExistingInfo = false,
  showShippingToggle = true,
  mode = "checkout", // 'checkout' | 'profile'
}) => {
  const { language } = useLanguage();
  const { currentUser, activeStoreId } = useUser();
  const [existingProfile, setExistingProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [showExistingInfoOption, setShowExistingInfoOption] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Translations
  const translations = {
    en: {
      customerInformation: "Customer Information",
      personalInformation: "Personal Information",
      billingAddress: "Billing Address",
      shippingAddress: "Shipping Address",
      firstName: "First Name",
      lastName: "Last Name",
      emailAddress: "Email Address",
      phoneNumber: "Phone Number",
      address: "Address",
      city: "City",
      stateEmirate: "State/Emirate",

      country: "Country",
      createAccount: "Create an account?",
      password: "Password",
      confirmPassword: "Confirm Password",
      sameAsBilling: "Shipping address same as billing",
      useExistingInfo: "Use my saved information",
      loadExistingInfo: "Load Saved Info",
      enterManually: "Enter Manually",
      required: "*",
      loading: "Loading...",
      countries: {
        UAE: "United Arab Emirates",
        KSA: "Saudi Arabia",
        QAT: "Qatar",
        KWT: "Kuwait",
        BHR: "Bahrain",
        OMN: "Oman",
      },
    },
    ar: {
      customerInformation: "معلومات العميل",
      personalInformation: "المعلومات الشخصية",
      billingAddress: "عنوان الفوترة",
      shippingAddress: "عنوان الشحن",
      firstName: "الاسم الأول",
      lastName: "اسم العائلة",
      emailAddress: "عنوان البريد الإلكتروني",
      phoneNumber: "رقم الهاتف",
      address: "العنوان",
      city: "المدينة",
      stateEmirate: "الولاية/الإمارة",

      country: "البلد",
      createAccount: "إنشاء حساب؟",
      password: "كلمة المرور",
      confirmPassword: "تأكيد كلمة المرور",
      sameAsBilling: "عنوان الشحن نفس عنوان الفوترة",
      useExistingInfo: "استخدم معلوماتي المحفوظة",
      loadExistingInfo: "تحميل المعلومات المحفوظة",
      enterManually: "إدخال يدوي",
      required: "*",
      loading: "جاري التحميل...",
      countries: {
        UAE: "دولة الإمارات العربية المتحدة",
        KSA: "المملكة العربية السعودية",
        QAT: "قطر",
        KWT: "الكويت",
        BHR: "البحرين",
        OMN: "عُمان",
      },
    },
  };

  const t = translations[language] || translations.en;

  // Load existing profile on mount
  useEffect(() => {
    const loadExistingProfile = async () => {
      if (currentUser && activeStoreId && showUseExistingInfo) {
        setLoadingProfile(true);
        try {
          const profile = await fetchStoreCustomerProfile(
            currentUser.uid,
            activeStoreId
          );
          if (profile) {
            setExistingProfile(profile);
            setShowExistingInfoOption(true);
          }
        } catch (error) {
          console.error("Error loading customer profile:", error);
        } finally {
          setLoadingProfile(false);
        }
      }
    };

    loadExistingProfile();
  }, [currentUser, activeStoreId, showUseExistingInfo]);

  // Handle input changes
  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      billingInfo: { ...prev.billingInfo, [name]: value },
    }));

    // Update shipping info if same as billing
    if (formData.sameAsBilling) {
      setFormData((prev) => ({
        ...prev,
        shippingInfo: { ...prev.shippingInfo, [name]: value },
      }));
    }
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      shippingInfo: { ...prev.shippingInfo, [name]: value },
    }));
  };

  const handleAccountChange = (field, value) => {
    if (field === "createAccount" && value) {
      // When create account is checked, show auth modal
      setShowAuthModal(true);
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    // Uncheck the create account checkbox when modal is closed
    setFormData((prev) => ({ ...prev, createAccount: false }));
  };

  // Get the email address to pass to auth modal
  const getEmailForAuth = () => {
    if (mode === "checkout") {
      return formData.billingInfo?.email || "";
    } else {
      return formData.email || "";
    }
  };

  const handleSameAsBillingChange = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      sameAsBilling: checked,
      shippingInfo: checked ? prev.billingInfo : prev.shippingInfo,
    }));
  };

  // Load existing profile data into form
  const loadExistingProfileData = () => {
    if (!existingProfile) return;

    // Use the new data structure: customerInfo, billingInfo, shippingInfo
    const customerInfo = existingProfile.customerInfo || {};
    const billingInfo = existingProfile.billingInfo || {};
    const shippingInfo = existingProfile.shippingInfo || {};

    setFormData((prev) => ({
      ...prev,
      billingInfo: {
        firstName: billingInfo.firstName || customerInfo.firstName || "",
        lastName: billingInfo.lastName || customerInfo.lastName || "",
        email: billingInfo.email || customerInfo.email || "",
        phone: billingInfo.phone || customerInfo.phone || "",
        address: billingInfo.address || customerInfo.address || "",
        city: billingInfo.city || customerInfo.city || "",
        state: billingInfo.state || customerInfo.state || "",
        zipCode: billingInfo.zipCode || customerInfo.zipCode || "",
        country: billingInfo.country || customerInfo.country || "UAE",
      },
      shippingInfo: {
        firstName: shippingInfo.firstName || customerInfo.firstName || "",
        lastName: shippingInfo.lastName || customerInfo.lastName || "",
        email: shippingInfo.email || customerInfo.email || "",
        phone: shippingInfo.phone || customerInfo.phone || "",
        address: shippingInfo.address || customerInfo.address || "",
        city: shippingInfo.city || customerInfo.city || "",
        state: shippingInfo.state || customerInfo.state || "",
        zipCode: shippingInfo.zipCode || customerInfo.zipCode || "",
        country: shippingInfo.country || customerInfo.country || "UAE",
      },
      sameAsBilling: true, // Default to same as billing
      // For profile mode
      displayName:
        customerInfo.firstName && customerInfo.lastName
          ? `${customerInfo.firstName} ${customerInfo.lastName}`
          : "",
      email: customerInfo.email || "",
      phone: customerInfo.phone || "",
      address: customerInfo.address || "",
      city: customerInfo.city || "",
      state: customerInfo.state || "",
      zipCode: customerInfo.zipCode || "",
      country: customerInfo.country || "UAE",
    }));
  };

  // Get country name in current language
  const getCountryName = (country) => {
    return t.countries[country] || country;
  };

  // Get available countries
  const getAvailableCountries = () => {
    if (
      storeSettings?.availableCountries &&
      storeSettings.availableCountries.length > 0
    ) {
      return storeSettings.availableCountries;
    }
    // Default countries
    return [{ code: "UAE", name: "United Arab Emirates" }];
  };

  return (
    <div
      className={`customer-info-form ${
        mode === "profile" ? "profile-mode" : ""
      }`}
    >
      {/* Use Existing Information Option */}
      {showUseExistingInfo && showExistingInfoOption && (
        <div className="existing-info-section">
          <div className="existing-info-header">
            <h3>{t.useExistingInfo}</h3>
            {loadingProfile ? (
              <span className="loading-text">{t.loading}</span>
            ) : (
              <button
                type="button"
                className="load-existing-btn"
                onClick={loadExistingProfileData}
                disabled={processing}
              >
                {t.loadExistingInfo}
              </button>
            )}
          </div>
          {existingProfile && (
            <div className="existing-info-preview">
              <p>
                <strong>
                  {existingProfile.firstName} {existingProfile.lastName}
                </strong>
              </p>
              <p>{existingProfile.email}</p>
              {existingProfile.billingAddress && (
                <p>
                  {existingProfile.billingAddress.address},{" "}
                  {existingProfile.billingAddress.city}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Customer Information Section */}
      <div className="form-section">
        <h2>
          {mode === "profile" ? t.personalInformation : t.customerInformation}
        </h2>

        {mode === "checkout" ? (
          <>
            {/* First Name and Last Name - Always shown in checkout */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="billing-firstName">
                  {t.firstName} {t.required}
                </label>
                <input
                  type="text"
                  id="billing-firstName"
                  name="firstName"
                  value={formData.billingInfo?.firstName || ""}
                  onChange={handleBillingChange}
                  required
                  disabled={processing}
                />
              </div>
              <div className="form-group">
                <label htmlFor="billing-lastName">
                  {t.lastName} {t.required}
                </label>
                <input
                  type="text"
                  id="billing-lastName"
                  name="lastName"
                  value={formData.billingInfo?.lastName || ""}
                  onChange={handleBillingChange}
                  required
                  disabled={processing}
                />
              </div>
            </div>

            {/* Email and Phone - Always shown in checkout */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="billing-email">
                  {t.emailAddress} {t.required}
                </label>
                <input
                  type="email"
                  id="billing-email"
                  name="email"
                  value={formData.billingInfo?.email || ""}
                  onChange={handleBillingChange}
                  required
                  disabled={processing}
                />
              </div>
              <div className="form-group">
                <label htmlFor="billing-phone">
                  {t.phoneNumber} {t.required}
                </label>
                <input
                  type="tel"
                  id="billing-phone"
                  name="phone"
                  value={formData.billingInfo?.phone || ""}
                  onChange={handleBillingChange}
                  required
                  disabled={processing}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label htmlFor="displayName">
                {t.firstName} {t.lastName} {t.required}
              </label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName || ""}
                onChange={handlePersonalChange}
                required
                disabled={processing}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">
                {t.emailAddress} {t.required}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email || ""}
                onChange={handlePersonalChange}
                required
                disabled={processing || mode === "profile"}
              />
              {mode === "profile" && <small>Email cannot be changed</small>}
            </div>
            <div className="form-group">
              <label htmlFor="phone">
                {t.phoneNumber} {t.required}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone || ""}
                onChange={handlePersonalChange}
                required
                disabled={processing}
              />
            </div>
          </>
        )}

        {/* Account Creation Option (for guest users in checkout) */}
        {showCreateAccount && !currentUser && mode === "checkout" && (
          <div className="account-creation">
            <div className="form-checkbox">
              <input
                type="checkbox"
                id="create-account"
                checked={formData.createAccount}
                onChange={(e) =>
                  handleAccountChange("createAccount", e.target.checked)
                }
                disabled={processing}
              />
              <label htmlFor="create-account">{t.createAccount}</label>
            </div>
          </div>
        )}
      </div>

      {/* Billing Address Section - Only show if shipping is required */}
      {showShippingToggle && (
        <div className="form-section">
          <h2>{t.billingAddress}</h2>

          <div className="form-group">
            <label htmlFor="billing-address">
              {t.address} {t.required}
            </label>
            <input
              type="text"
              id="billing-address"
              name="address"
              value={formData.billingInfo?.address || formData.address || ""}
              onChange={
                mode === "profile" ? handlePersonalChange : handleBillingChange
              }
              required
              disabled={processing}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="billing-city">
                {t.city} {t.required}
              </label>
              <input
                type="text"
                id="billing-city"
                name="city"
                value={formData.billingInfo?.city || formData.city || ""}
                onChange={
                  mode === "profile"
                    ? handlePersonalChange
                    : handleBillingChange
                }
                required
                disabled={processing}
              />
            </div>
            <div className="form-group">
              <label htmlFor="billing-state">
                {t.stateEmirate} {t.required}
              </label>
              <input
                type="text"
                id="billing-state"
                name="state"
                value={formData.billingInfo?.state || formData.state || ""}
                onChange={
                  mode === "profile"
                    ? handlePersonalChange
                    : handleBillingChange
                }
                required
                disabled={processing}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="billing-country">
                {t.country} {t.required}
              </label>
              <select
                id="billing-country"
                name="country"
                value={
                  formData.billingInfo?.country || formData.country || "UAE"
                }
                onChange={
                  mode === "profile"
                    ? handlePersonalChange
                    : handleBillingChange
                }
                required
                disabled={processing}
              >
                {getAvailableCountries().map((country) => (
                  <option key={country.code} value={country.code}>
                    {getCountryName(country.code)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Same as Billing Checkbox */}
          {showShippingToggle && (
            <div className="form-checkbox">
              <input
                type="checkbox"
                id="same-as-billing"
                checked={formData.sameAsBilling}
                onChange={handleSameAsBillingChange}
                disabled={processing}
              />
              <label htmlFor="same-as-billing">{t.sameAsBilling}</label>
            </div>
          )}
        </div>
      )}

      {/* Shipping Address Section */}
      {showShippingToggle && !formData.sameAsBilling && (
        <div className="form-section">
          <h2>{t.shippingAddress}</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="shipping-firstName">
                {t.firstName} {t.required}
              </label>
              <input
                type="text"
                id="shipping-firstName"
                name="firstName"
                value={formData.shippingInfo?.firstName || ""}
                onChange={handleShippingChange}
                required
                disabled={processing}
              />
            </div>
            <div className="form-group">
              <label htmlFor="shipping-lastName">
                {t.lastName} {t.required}
              </label>
              <input
                type="text"
                id="shipping-lastName"
                name="lastName"
                value={formData.shippingInfo?.lastName || ""}
                onChange={handleShippingChange}
                required
                disabled={processing}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="shipping-address">
              {t.address} {t.required}
            </label>
            <input
              type="text"
              id="shipping-address"
              name="address"
              value={formData.shippingInfo?.address || ""}
              onChange={handleShippingChange}
              required
              disabled={processing}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="shipping-city">
                {t.city} {t.required}
              </label>
              <input
                type="text"
                id="shipping-city"
                name="city"
                value={formData.shippingInfo?.city || ""}
                onChange={handleShippingChange}
                required
                disabled={processing}
              />
            </div>
            <div className="form-group">
              <label htmlFor="shipping-state">
                {t.stateEmirate} {t.required}
              </label>
              <input
                type="text"
                id="shipping-state"
                name="state"
                value={formData.shippingInfo?.state || ""}
                onChange={handleShippingChange}
                required
                disabled={processing}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="shipping-country">
                {t.country} {t.required}
              </label>
              <select
                id="shipping-country"
                name="country"
                value={formData.shippingInfo?.country || "UAE"}
                onChange={handleShippingChange}
                required
                disabled={processing}
              >
                {getAvailableCountries().map((country) => (
                  <option key={country.code} value={country.code}>
                    {getCountryName(country.code)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <>
          <div className="auth-modal-overlay" onClick={handleAuthModalClose} />
          <div className="auth-modal">
            <AuthPage
              panelMode={true}
              onClose={handleAuthModalClose}
              initialEmail={getEmailForAuth()}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerInfoForm;
